import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { CopyableCell } from '../../components/ui/CopyableCell';
import { CopyableLinkCell } from '../../components/ui/CopyableLinkCell';
import { useCurrentUser } from '../auth';
import { listCampaigns } from '../campaigns/api';
import { centsToDisplay } from '../../lib/money';
import { isBudgetModified } from '../../lib/dailyBudget';
import { effectiveMetaAdType } from '../../lib/effectiveMetaAdType';
import { effectiveRunsMeta } from '../../lib/platformEnabled';
import { resolveGroupTag } from '../../lib/groupTag';
import {
  hasEndDateDrift,
  hasRowModifications,
  hasStartDateDrift,
} from './hasRowModifications';
import { resolveRowLandingUrl } from '../../lib/landingPageResolution';
import { resolveRowMetaName } from '../../lib/resolveRowTemplates';
import {
  buildRowRenderContext,
  renderRowTemplate,
  renderRowTemplateArray,
} from '../../lib/rowContent';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { ApiError } from '../../lib/api';
import type {
  AccountCampaign,
  AccountCampaignBulkAction,
  AccountCampaignBulkResult,
  AccountCampaignInlinePatch,
} from '../../types/accountCampaign';
import type { Campaign, CampaignLeadForm, CtaOption, LeadFormText, MetaAdCopy, UtmConfig } from '../../types/campaign';
import { bulkAction, getRolloutRows, listUsers, patchRow, refreshFields } from './api';
import { bulkActionLabel } from './bulkActionLabels';
import { BulkRefreshFromCampaignModal } from './BulkRefreshFromCampaignModal';
import { BulkRejectionToast } from './BulkRejectionToast';
import { DailyBudgetCell } from './DailyBudgetCell';
import { GridBulkExecutionBar } from './GridBulkExecutionBar';
import { NotesCell } from './NotesCell';
import { ReadOnlyArrayCell } from './ReadOnlyArrayCell';
import { RolloutTabStrip } from './RolloutTabStrip';
import { computeGridCounts } from './rolloutGridCounts';
import {
  computeMetaBuildCounters,
  type MetaBuildCounters,
} from './metaBuildCounters';
import {
  shouldRenderMetaLocalBuiltCell,
  shouldRenderMetaOrganicBuiltCell,
} from './columns';
import { BuilderCell } from './BuilderCell';
import { BuildViewLink } from './BuildViewLink';
import { GroupBadge } from './GroupBadge';
import { GridFilterBar } from './GridFilterBar';
import {
  DEFAULT_GRID_FILTERS,
  readGridFiltersFromParams,
  writeGridFiltersToParams,
  type GridFilters,
} from '../../lib/gridFilterUrl';
import { applyGridFilters, deriveGridOptions } from '../../lib/applyGridFilters';
import {
  compareRows,
  DEFAULT_SORT,
  type SortLookups,
  type SortState,
} from './gridSort';
import { SortableHeaderButton } from './SortableHeaderButton';

// P2b-3 §3: Meta execution grid. Read-mostly view for the Meta team —
// Context + Budget columns are read-only (commit 3); execution checkboxes
// + bulk actions land in commit 4. Click-to-copy on resolved name + URLs
// arrives in commit 6 (CopyableCell).
//
// Data flow:
//   - Display rows: server gridFilter='meta' query, deduped per spec §2.3.
//   - Tab counts:   full master rows query (also used by RolloutDetail —
//                   react-query dedupes by queryKey).
//   - Campaigns:    brand-scoped, used for ad-type resolution + content
//                   columns (ad copy lives on Campaign post-snapshot).
//   - Users:        AM column display names.

export function MetaGrid() {
  const { id } = useParams<{ id: string }>();
  const currentUser = useCurrentUser();
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRefreshTarget, setBulkRefreshTarget] = useState<{
    campaign: Campaign;
    selectedRows: AccountCampaign[];
  } | null>(null);
  const [bulkResult, setBulkResult] = useState<
    null | { label: string; result: AccountCampaignBulkResult }
  >(null);

  // P2b-3 §6.1 filter state — seeded from URL on mount, then bidirectional.
  // We deliberately don't continuously sync URL → state to avoid
  // feedback loops; back/forward navigation re-mounts the page so the
  // initial-read path covers it.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = useMemo(
    () => readGridFiltersFromParams(searchParams, 'meta'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [filters, setFilters] = useState<GridFilters>(initialFilters);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    writeGridFiltersToParams(next, filters);
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Sort state — transient (per spec §6.2 it's NOT in the URL).
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  // Filtered (display) rows.
  const metaRowsQuery = useQuery({
    queryKey: ['rollout', id, 'rows', 'meta'],
    queryFn: () => getRolloutRows(id!, { gridFilter: 'meta' }),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  // Full master rows for tab counts. Same response shape, different
  // queryKey — caches separately from the filtered fetch and is shared
  // with RolloutDetail / GoogleGrid via the same key.
  const masterRowsQuery = useQuery({
    queryKey: ['rollout', id, 'rows'],
    queryFn: () => getRolloutRows(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const brandAccountId = metaRowsQuery.data?.rollout.brandAccountId;
  const campaignsQuery = useQuery({
    queryKey: ['campaigns', 'brand', brandAccountId],
    queryFn: () => listCampaigns({ brandAccountId: brandAccountId! }),
    enabled: !!brandAccountId,
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => listUsers(),
  });

  const allCampaigns = campaignsQuery.data ?? [];
  const allUsers = usersQuery.data ?? [];

  const accountsById = useMemo(() => {
    const accs = metaRowsQuery.data?.accounts ?? [];
    return new Map(accs.map((a) => [a.id, a]));
  }, [metaRowsQuery.data]);
  const campaignsById = useMemo(
    () => new Map(allCampaigns.map((c) => [c.id, c])),
    [allCampaigns],
  );
  const usersById = useMemo(() => new Map(allUsers.map((u) => [u.uid, u])), [allUsers]);
  const profileById = useMemo(() => {
    const profiles = metaRowsQuery.data?.locationProfiles ?? [];
    return new Map(profiles.map((p) => [p.accountId, p]));
  }, [metaRowsQuery.data]);

  // Counts derived from the master rows query so they're stable as the
  // filtered grid's filter-bar (commit 7) changes.
  const counts = useMemo(() => {
    const masterRows = masterRowsQuery.data?.rows ?? [];
    const masterAccounts = masterRowsQuery.data?.accounts ?? [];
    const masterProfiles = masterRowsQuery.data?.locationProfiles ?? [];
    const archived = masterRowsQuery.data?.rollout.status === 'archived';
    return computeGridCounts({
      rows: masterRows,
      accountsById: new Map(masterAccounts.map((a) => [a.id, a])),
      campaignsById: new Map(allCampaigns.map((c) => [c.id, c])),
      locationProfilesById: new Map(masterProfiles.map((p) => [p.accountId, p])),
      rolloutArchived: !!archived,
    });
  }, [masterRowsQuery.data, allCampaigns]);

  // Item 6: big header counters. Computed from the already-fetched master
  // rows (no new endpoint) so they reflect the whole rollout, stable as
  // the grid's own filter-bar changes.
  const buildCounters = useMemo(
    () => computeMetaBuildCounters(masterRowsQuery.data?.rows ?? []),
    [masterRowsQuery.data],
  );

  // Inline-patch mutation (per-row execution checkbox flips, Preview
  // Links, Notes). Refetches both the filtered Meta query and the master
  // query so tab counts update if a flip changes the gate.
  const patchMut = useMutation({
    mutationFn: ({ rowId, patch }: { rowId: string; patch: AccountCampaignInlinePatch }) =>
      patchRow(id!, rowId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['rollout', id, 'rows', 'meta'] });
    },
  });
  const patchField = (rowId: string, patch: AccountCampaignInlinePatch) => {
    patchMut.mutate({ rowId, patch });
  };
  const patchFieldAsync = async (rowId: string, patch: AccountCampaignInlinePatch) => {
    await patchMut.mutateAsync({ rowId, patch });
  };

  // Bulk-action mutation. Reuses the existing /rows/bulk endpoint with the
  // new execution-checkbox kinds added in this commit.
  const bulkMut = useMutation({
    mutationFn: async (action: AccountCampaignBulkAction) => bulkAction(id!, action),
  });
  const dispatchBulk = async (action: AccountCampaignBulkAction) => {
    try {
      const result = await bulkMut.mutateAsync(action);
      setBulkResult({ label: bulkActionLabel(action), result });
      await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
      await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows', 'meta'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setBulkResult({
        label: bulkActionLabel(action),
        result: {
          succeeded: 0,
          rejected: [{ accountCampaignId: '(none)', locationName: '(all rows)', reasons: [msg] }],
        },
      });
    }
  };

  const toggleSelect = (rowId: string, next: boolean) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(rowId);
      else copy.delete(rowId);
      return copy;
    });
  };

  if (
    metaRowsQuery.isLoading ||
    masterRowsQuery.isLoading ||
    campaignsQuery.isLoading ||
    usersQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  if (metaRowsQuery.error || !metaRowsQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load Meta grid: {(metaRowsQuery.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  const { rollout, rows: gateRows, brandFranchiseProfile } = metaRowsQuery.data;
  const brandAccount = accountsById.get(rollout.brandAccountId);
  const archived = rollout.status === 'archived';

  // Filter-bar options derive from gate-passing rows so toggling filters
  // doesn't shrink the dropdown's own option list.
  const filterOptions = deriveGridOptions({
    rows: gateRows,
    accountsById,
    campaignsById,
    usersById,
    platform: 'meta',
  });

  // Filter → sort. Both pure helpers; recomputing inline is cheap.
  const filteredRows = applyGridFilters({
    rows: gateRows,
    filters,
    accountsById,
    campaignsById,
    usersById,
    platform: 'meta',
    currentUserUid: currentUser.uid,
  });
  const sortLookups: SortLookups = { accountsById, campaignsById, usersById };
  const rows = sort
    ? [...filteredRows].sort((a, b) => compareRows(a, b, sort, sortLookups))
    : filteredRows;

  // Header colspan tally (kept here so the colSpan counts and column
  // count for the empty-state row stay in sync if the column set
  // shifts):
  //   Context: 15 | Budget: 4 | Execution: 6
  //   Selection checkbox col: 1 (hidden when archived)
  // Round-1 polish Fix 6: +1 Context column for the per-row Meta builder.
  // Round-2 polish Fix 3: +1 Context column for Local Assets (sits
  // between Asset folder and Meta ad account).
  // OrganicAssets §6.1: +1 Context column for Organic Assets (after Local),
  // +1 Execution column for Organic Built (after Local Built).
  const contextCols = 17;
  const budgetCols = 4;
  const executionCols = 7;
  const totalCols = (archived ? 0 : 1) + contextCols + budgetCols + executionCols;

  const allSelected = !archived && rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const toggleAll = (next: boolean) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      for (const r of rows) {
        if (next) copy.add(r.id);
        else copy.delete(r.id);
      }
      return copy;
    });
  };

  return (
    <div className="space-y-4">
      <RolloutTabStrip
        rolloutId={rollout.id}
        active="meta"
        metaCount={counts.meta}
        googleCount={counts.google}
      />

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-slate-500">
            <Link to="/rollouts" className="hover:underline">Rollouts</Link>
            <span className="mx-1 text-slate-300">/</span>
            {brandAccount ? (
              <Link to={`/accounts/${brandAccount.id}`} className="hover:underline">
                {brandAccount.name}
              </Link>
            ) : null}
            <span className="mx-1 text-slate-300">/</span>
            <Link to={`/rollouts/${rollout.id}`} className="hover:underline">
              {rollout.name}
            </Link>
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">Meta build</h1>
          <p className="text-sm text-slate-500 mt-1">
            {gateRows.length === rows.length
              ? `${rows.length} ${rows.length === 1 ? 'row' : 'rows'} ready to build`
              : `${rows.length} of ${gateRows.length} ${gateRows.length === 1 ? 'row' : 'rows'} shown`}
            {rollout.monthYearLabel ? ` · ${rollout.monthYearLabel}` : ''}
            {archived && (
              <span className="ml-2 text-xs uppercase tracking-wide text-slate-400">
                read-only (archived)
              </span>
            )}
          </p>
        </div>
      </div>

      <MetaBuildCounterStrip counters={buildCounters} />

      <GridFilterBar
        filters={filters}
        onChange={setFilters}
        amsInGrid={filterOptions.ams}
        campaignsInGrid={filterOptions.campaigns}
        groupsInGrid={filterOptions.groups}
        hasNoGroup={filterOptions.hasNoGroup}
        buildersInGrid={filterOptions.builders}
        hasUnassignedBuilder={filterOptions.hasUnassignedBuilder}
        platform="meta"
      />

      <GridBulkExecutionBar
        platform="meta"
        selectedIds={[...selectedIds]}
        onClear={() => setSelectedIds(new Set())}
        onDispatch={dispatchBulk}
        archived={archived}
        users={allUsers}
        rows={rows}
        campaigns={allCampaigns}
        onRefreshFromCampaign={(c) =>
          setBulkRefreshTarget({
            campaign: c,
            selectedRows: rows.filter((r) => selectedIds.has(r.id)),
          })
        }
      />

      {bulkRefreshTarget && (
        <BulkRefreshFromCampaignModal
          campaign={bulkRefreshTarget.campaign}
          selectedRows={bulkRefreshTarget.selectedRows}
          onSubmit={async (fieldKeys) => {
            await refreshFields(
              id!,
              bulkRefreshTarget.selectedRows.map((r) => r.id),
              fieldKeys,
            );
            await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
            await qc.invalidateQueries({
              queryKey: ['rollout', id, 'rows', 'meta'],
            });
          }}
          onClose={() => setBulkRefreshTarget(null)}
        />
      )}

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="min-w-max text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {!archived && (
                <th
                  rowSpan={2}
                  className="px-2 py-2 bg-slate-50 border-b border-slate-200"
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleAll(e.target.checked)}
                    aria-label="Select all visible rows"
                  />
                </th>
              )}
              <th
                colSpan={contextCols}
                className="px-2 py-2 font-semibold text-center bg-slate-50 border-b border-slate-200"
              >
                Context
              </th>
              <th
                colSpan={budgetCols}
                className="px-2 py-2 font-semibold text-center bg-slate-100 border-b border-slate-200"
              >
                Budget
              </th>
              <th
                colSpan={executionCols}
                className="px-2 py-2 font-semibold text-center bg-slate-50 border-b border-slate-200"
              >
                Execution
              </th>
            </tr>
            <tr>
              {/* Context columns */}
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 sticky left-0 bg-slate-50 z-10">
                <SortableHeaderButton column="location" label="Location" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="am" label="AM" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                Builder
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="campaign" label="Campaign" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                Resolved Meta name
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Ad type</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Body copy</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Headlines</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Descriptions</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">CTA</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Lead form</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Landing URL</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">UTM</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Asset folder</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Local assets</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Organic assets</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Meta ad account</th>
              {/* Budget columns */}
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">Monthly</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">Increase</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">Daily</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">
                <SortableHeaderButton column="startDate" label="Dates" sort={sort} onChange={setSort} />
              </th>
              {/* Execution columns */}
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="metaBuilt" label="Main Built" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Local Built</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Organic Built</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="metaLive" label="Scheduled/Live" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Post-Check</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Preview Links</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={totalCols}
                  className="px-4 py-6 text-center text-slate-500 text-sm"
                >
                  {gateRows.length === 0 ? (
                    'No rows ready for Meta build. AMs need to mark rows ready and set budgets.'
                  ) : (
                    <>
                      No rows match these filters.{' '}
                      <button
                        type="button"
                        onClick={() => setFilters(DEFAULT_GRID_FILTERS)}
                        className="text-blue-600 hover:underline"
                      >
                        Clear filters
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const acc = accountsById.get(row.accountId);
                if (!acc) return null;
                const campaign = row.campaignId ? campaignsById.get(row.campaignId) ?? null : null;
                const profile = profileById.get(acc.id) ?? null;
                const am = row.assignedAmId ? usersById.get(row.assignedAmId) ?? null : null;
                const adType = effectiveMetaAdType(
                  { metaAdType: row.metaAdType ?? null },
                  campaign ?? { metaAdCopy: { metaAdType: 'traffic' } },
                  profile,
                );
                const adTypeLabel = adType === 'traffic' ? 'Website Lead' : adType === 'lead_gen' ? 'Meta Lead Gen' : '—';
                const showLeadForm = adType === 'lead_gen';
                const adCopy: MetaAdCopy | null = row.metaAdCopy;
                // P2b-3 round-1 polish Fix 3: substitute {locationName} /
                // {suburb} / {gymInfo} / {campaignDescriptor} / {monthYear}
                // in body copy + headlines + descriptions before display.
                const renderCtx = buildRowRenderContext({
                  row,
                  campaign,
                  account: acc,
                  locationProfile: profile,
                  brandProfile: brandFranchiseProfile,
                  rollout,
                });
                const bodyCopyRendered = renderRowTemplateArray(adCopy?.bodyCopy ?? null, renderCtx);
                const headlinesRendered = renderRowTemplateArray(adCopy?.headlines ?? null, renderCtx);
                const descriptionsRendered = renderRowTemplateArray(
                  adCopy?.descriptions ?? null,
                  renderCtx,
                );
                const utm: UtmConfig | null = row.utmConfig;
                const resolvedName = resolveRowMetaName({
                  row: {
                    campaignDescriptor: row.campaignDescriptor,
                    metaCampaignNameTemplate: row.metaCampaignNameTemplate,
                    googleAdLabelTemplate: row.googleAdLabelTemplate,
                  },
                  campaign,
                  account: acc,
                  locationProfile: profile,
                  brandProfile: brandFranchiseProfile,
                  rollout,
                });
                // Round-1 polish follow-up: resolved landing URL still
                // carries placeholders from the brand template (e.g.
                // {slug}, {suburbSlug}, {shortSuburb}) — pipe it
                // through renderRowTemplate so the buyer pastes a
                // location-substituted URL, not the template literal.
                const landingUrlRaw = resolveRowLandingUrl(
                  row,
                  brandFranchiseProfile,
                  profile,
                  'meta',
                );
                const landingUrl = renderRowTemplate(landingUrlRaw, renderCtx) || null;
                const assetFolder = row.metaAssetsFolderUrl ?? campaign?.metaAssetsFolderUrl ?? null;
                const dateRange = formatDateRange(row.startDate, row.endDate);
                const metaActive = effectiveRunsMeta(acc.runsMeta, row.metaEnabled, campaign?.channels);
                // metaActive is true for every row that passes the §2.1
                // gate, so this is informational — kept for symmetry with
                // the Master grid's runtime check.
                void metaActive;
                const locationLabel = profile?.franchiseSuburb?.trim() || acc.name;
                // Group pill mirrors the Master grid (parent-chain walk).
                const group = resolveGroupTag(acc.id, accountsById);
                // Round-2 polish Fix 4: modification signals mirroring
                // the master grid. hasRowModifications gates internally
                // on opt-out / unassigned; hasStart/EndDateDrift gate
                // on opt-out + legacy-undefined snapshots.
                const showContentDot = hasRowModifications(row, campaign);
                const startDrift = hasStartDateDrift(row);
                const endDrift = hasEndDateDrift(row);
                const showDateDot = startDrift || endDrift;
                const dateDotTitle =
                  startDrift && endDrift
                    ? 'Both dates modified'
                    : startDrift
                      ? 'Start date modified'
                      : 'End date modified';
                return (
                  <tr
                    key={row.id}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${
                      !row.primary ? 'bg-slate-50/40' : ''
                    }`}
                  >
                    {!archived && (
                      <td className="px-2 py-1.5 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.id)}
                          onChange={(e) => toggleSelect(row.id, e.target.checked)}
                          aria-label={`Select ${acc.name}`}
                        />
                      </td>
                    )}
                    <td className="px-2 py-1.5 align-top sticky left-0 bg-white">
                      <div className="font-medium inline-flex items-center gap-1.5">
                        <BuildViewLink
                          rolloutId={rollout.id}
                          rowId={row.id}
                          platform="meta"
                          locationLabel={locationLabel}
                          filteredRowIds={rows.map((r) => r.id)}
                        />
                        <Link to={`/accounts/${acc.id}`} className="hover:underline" title={acc.name}>
                          {locationLabel}
                        </Link>
                        <GroupBadge name={group?.name ?? null} />
                        {showContentDot && (
                          // Round-2 polish Fix 4: 8px orange content
                          // modification dot — same styling as the
                          // master grid's pencil-icon dot.
                          <span
                            aria-hidden="true"
                            title="Row content modified from Campaign"
                            className="inline-block w-2 h-2 rounded-full bg-orange-500"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-top text-slate-700 whitespace-nowrap">
                      {am ? formatUserDisplayName(am) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      <BuilderCell
                        value={row.metaBuilderId}
                        users={allUsers}
                        disabled={archived}
                        ariaLabel={`Meta builder for ${acc.name}`}
                        onChange={(next) => patchField(row.id, { metaBuilderId: next })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {row.campaignNameOverride ?? campaign?.name ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {resolvedName.resolved ? (
                        <CopyableCell
                          value={resolvedName.resolved}
                          ariaLabel={`Resolved Meta campaign name for ${acc.name}`}
                          display={
                            <span className="font-mono text-xs">{resolvedName.resolved}</span>
                          }
                        />
                      ) : (
                        <span className="text-slate-400" title="No naming template set">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">{adTypeLabel}</td>
                    <td className="px-2 py-1.5 align-top">
                      <ReadOnlyArrayCell
                        values={bodyCopyRendered}
                        mode="first-line"
                        ariaLabel={`Body copy for ${acc.name}`}
                        popoverTitle={`Body copy — ${acc.name}`}
                        className="w-56"
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <ReadOnlyArrayCell
                        values={headlinesRendered}
                        mode="first-three-list"
                        ariaLabel={`Headlines for ${acc.name}`}
                        popoverTitle={`Headlines — ${acc.name}`}
                        className="w-48"
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <ReadOnlyArrayCell
                        values={descriptionsRendered}
                        mode="first-three-list"
                        ariaLabel={`Descriptions for ${acc.name}`}
                        popoverTitle={`Descriptions — ${acc.name}`}
                        className="w-48"
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {renderCta(adCopy?.ctaButton ?? null)}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {showLeadForm ? (
                        renderLeadFormSummary(row.leadForm)
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <CopyableLinkCell
                        href={landingUrl}
                        ariaLabel={`Landing URL for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {renderUtmSummary(utm, 'meta')}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <CopyableLinkCell
                        href={assetFolder}
                        display={<span>Open</span>}
                        ariaLabel={`Meta asset folder for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {/* Round-2 polish Fix 3: Local Assets URL is per-
                          row data — buyers reference it but edits go
                          via the master grid / row editor. */}
                      <CopyableLinkCell
                        href={row.localAssetsUrl}
                        display={<span>Open</span>}
                        ariaLabel={`Local assets for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {/* OrganicAssets §6.1: Organic Assets folder, mirrors
                          Local. Per-row data; edits via master grid / row
                          editor. */}
                      <CopyableLinkCell
                        href={row.organicAssetsUrl ?? null}
                        display={<span>Open</span>}
                        ariaLabel={`Organic assets for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <CopyableLinkCell
                        href={acc.fbAdAccountUrl}
                        display={<span>Open</span>}
                        ariaLabel={`Meta ad account for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {centsToDisplay(row.metaMonthlyBudget, acc.currency)}
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {row.metaCampaignIncrease > 0
                        ? centsToDisplay(row.metaCampaignIncrease, acc.currency)
                        : '—'}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <DailyBudgetCell
                        row={row}
                        platform="meta"
                        currency={acc.currency}
                        modified={isBudgetModified(row, 'meta')}
                        accountMonthlyBudget={acc.metaMasterMonthlyBudget}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap text-xs text-slate-700">
                      <span className="inline-flex items-center gap-1.5">
                        {dateRange}
                        {showDateDot && (
                          // Round-2 polish Fix 4: single dot when either
                          // date drifts — the role grids merge start +
                          // end into one cell, unlike master.
                          <span
                            aria-hidden="true"
                            title={dateDotTitle}
                            className="inline-block w-2 h-2 rounded-full bg-orange-500"
                          />
                        )}
                      </span>
                    </td>
                    {/* Execution cells */}
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="checkbox"
                        checked={row.metaBuilt}
                        disabled={archived}
                        onChange={(e) => patchField(row.id, { metaBuilt: e.target.checked })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {shouldRenderMetaLocalBuiltCell(row) ? (
                        <input
                          type="checkbox"
                          checked={row.metaLocalBuilt}
                          disabled={archived}
                          onChange={(e) =>
                            patchField(row.id, { metaLocalBuilt: e.target.checked })
                          }
                        />
                      ) : (
                        <span className="text-slate-300" title="No Local files yet">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {shouldRenderMetaOrganicBuiltCell(row) ? (
                        <input
                          type="checkbox"
                          checked={row.metaOrganicBuilt}
                          disabled={archived}
                          onChange={(e) =>
                            patchField(row.id, { metaOrganicBuilt: e.target.checked })
                          }
                        />
                      ) : (
                        <span className="text-slate-300" title="No Organic files yet">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="checkbox"
                        checked={row.metaTurnedOn}
                        disabled={archived}
                        onChange={(e) => patchField(row.id, { metaTurnedOn: e.target.checked })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="checkbox"
                        checked={row.metaPostRolloutCheck}
                        disabled={archived}
                        onChange={(e) =>
                          patchField(row.id, { metaPostRolloutCheck: e.target.checked })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <PreviewLinksInput
                        value={row.metaPreviewLinks}
                        archived={archived}
                        ariaLabel={`Meta preview links for ${acc.name}`}
                        onCommit={(next) => patchField(row.id, { metaPreviewLinks: next })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <NotesCell
                        value={row.rowNotes}
                        locationLabel={locationLabel}
                        archived={archived}
                        onSave={(next) => patchFieldAsync(row.id, { rowNotes: next })}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {bulkResult && (
        <BulkRejectionToast
          actionLabel={bulkResult.label}
          result={bulkResult.result}
          onDismiss={() => setBulkResult(null)}
        />
      )}
    </div>
  );
}

// ---------- helpers ----------

// Item 6: large, scan-from-across-the-room counters. Green when the
// metric is 100% complete (and there's at least one row), neutral
// otherwise. The counters have independent denominators: Main Built is
// over all non-opt-out rows; Local / Organic Campaign Builds are over only
// the rows whose Local / Organic folder has files.
function MetaBuildCounterStrip({
  counters,
}: {
  counters: MetaBuildCounters;
}) {
  const { total, built, localTotal, localBuilt, organicTotal, organicBuilt } = counters;
  return (
    <div className="flex flex-wrap gap-4">
      <CounterCard label="Main Built" num={built} den={total} />
      <CounterCard
        label="Local Campaign Builds"
        num={localBuilt}
        den={localTotal}
      />
      <CounterCard
        label="Organic Campaign Builds"
        num={organicBuilt}
        den={organicTotal}
      />
    </div>
  );
}

function CounterCard({
  label,
  num,
  den,
}: {
  label: string;
  num: number;
  den: number;
}) {
  const complete = den > 0 && num >= den;
  return (
    <div
      className={`flex items-baseline gap-3 rounded-lg border px-5 py-3 ${
        complete
          ? 'border-emerald-300 bg-emerald-50'
          : 'border-slate-200 bg-white'
      }`}
    >
      <span
        className={`text-4xl font-bold tabular-nums leading-none ${
          complete ? 'text-emerald-700' : 'text-slate-900'
        }`}
      >
        {num}
        <span
          className={
            complete ? 'text-emerald-400' : 'text-slate-400'
          }
        >
          {' '}
          / {den}
        </span>
      </span>
      <span className="text-sm font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
    </div>
  );
}

function formatDateRange(startIso: string | null, endIso: string | null): string {
  const fmt = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const a = fmt(startIso);
  const b = fmt(endIso);
  if (!a && !b) return '—';
  if (a && b) return `${a} → ${b}`;
  return a ?? b ?? '—';
}

function renderCta(cta: CtaOption | null) {
  if (!cta) return <span className="text-slate-400">—</span>;
  return <span className="text-xs">{cta}</span>;
}

// Multi Lead Form spec §1.2. The row's frozen snapshot drives the summary;
// orphan / unassigned rows pass null and render the em-dash.
function renderLeadFormSummary(leadForm: CampaignLeadForm | null) {
  if (!leadForm) return <span className="text-slate-300">—</span>;
  const headline = describeLeadFormText(leadForm.fields.headline);
  return (
    <span className="text-xs text-slate-700">
      {leadForm.name} · {leadForm.formType} · {headline}
    </span>
  );
}

function describeLeadFormText(value: LeadFormText): string {
  return value.mode === 'Generic' ? 'Generic' : 'Custom';
}

// P2b-3 §3.3 / §4.3: per-row Preview Links input. Buyer pastes the Ads
// Manager / Google Ads preview URL after building. Commits on blur or
// Enter; archived rollouts render the input as read-only.
function PreviewLinksInput({
  value,
  archived,
  ariaLabel,
  onCommit,
}: {
  value: string | null;
  archived: boolean;
  ariaLabel: string;
  onCommit: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState<string>(value ?? '');
  // Pull `value` into local state so external refetches don't clobber an
  // in-progress edit. Resync on `value` change when the input is not
  // focused — implemented via a key-on-value pattern is simpler:
  // re-render with the new initial value when committed. Here we accept
  // last-write-wins on the wire.
  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const trimmed = draft.trim();
        const next = trimmed.length === 0 ? null : trimmed;
        if (next !== (value ?? null)) onCommit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(value ?? '');
          (e.target as HTMLInputElement).blur();
        }
      }}
      readOnly={archived}
      placeholder={archived ? '' : 'Paste preview URL'}
      aria-label={ariaLabel}
      className="w-44 text-xs rounded border border-slate-200 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-400"
    />
  );
}

function renderUtmSummary(utm: UtmConfig | null, platform: 'meta' | 'google') {
  if (!utm || !utm.enabled) return <span className="text-slate-400 text-xs">disabled</span>;
  if (!utm.platforms.includes(platform)) {
    return <span className="text-slate-400 text-xs">off ({platform})</span>;
  }
  const campaignName = utm.shared.campaignName ?? null;
  const platformConfig = platform === 'meta' ? utm.meta : utm.google;
  // Keep the cell tight — the buyer reads UTM detail in the row editor.
  // Tooltip surfaces source/medium/term/content for a quick scan.
  const tooltip = platformConfig
    ? `source=${platformConfig.source} · medium=${platformConfig.medium} · term=${platformConfig.term} · content=${platformConfig.content}`
    : undefined;
  return (
    <span className="text-xs text-slate-700" title={tooltip}>
      on{campaignName ? ` · ${campaignName}` : ''}
    </span>
  );
}
